import classNames from "classnames";
import React from "react";
import { Link } from "react-router-dom";
import { Flag } from "../../graphic/Flag";
import { Icon } from "../../graphic/Icon";
import { LoaderHorizontal } from "../../loaders/LoaderHorizontal";
import { Spinner } from "../../loaders/Spinner";
import styles from "./Button.scss";

export const Button = React.forwardRef(function Button(
    {
        name,
        title,
        type,
        link,
        href,
        onClick,

        icon,
        flag,

        color = "default",
        size,
        fill,

        circle,
        square,
        pill,

        iconRight,
        iconColor,
        hideIcon,

        disabled,
        active,
        busy,

        count,

        children,
        propagate = false,
        className
    },
    ref
) {
    const handleClick = (event) => {
        // Event propagation
        if (!propagate) {
            event.stopPropagation();
        }

        // Handler
        if (onClick) {
            onClick(event);
        }
    };

    const isDisabled = busy || disabled;
    const classes = classNames(
        styles.base,
        {
            [styles.colorDefault]: color == null || color === "default",
            [styles.colorPrimary]: color === "primary",
            [styles.colorSuccess]: color === "success",
            [styles.colorError]: color === "error",
            [styles.colorLight]: color === "light",
            [styles.colorSilent]: color === "silent",
            [styles.colorSilentError]: color === "silentError",
            [styles.colorToolbar]: color === "toolbar",

            [styles.sizeSmall]: size === "small",
            [styles.sizeBig]: size === "big",

            [styles.fill]: fill && fill !== "sm" && fill !== "md",
            [styles.fillSm]: fill === "sm",
            [styles.fillMd]: fill === "md",
            [styles.circle]: circle,
            [styles.square]: square,
            [styles.pill]: pill,

            [styles.iconPrimary]: iconColor === "primary",
            [styles.iconSuccess]: iconColor === "success",
            [styles.iconWarning]: iconColor === "warning",
            [styles.iconError]: iconColor === "error",

            [styles.active]: active && !disabled,

            [styles.iconRight]: iconRight,
            [styles.hideIcon]: hideIcon,

            [styles.busy]: busy,
            [styles.disabled]: isDisabled
        },
        className
    );

    const content = (
        <>
            {/* Busy */}
            {busy ? (
                <div
                    className={classNames(styles.loader, {
                        [styles.spinner]: square || circle
                    })}
                >
                    {square || circle ? <Spinner color="primary" /> : <LoaderHorizontal />}
                </div>
            ) : null}

            {/* Icon left */}
            {icon && !iconRight ? <Icon className={styles.icon} name={icon} /> : null}

            {/* Flag */}
            {flag ? <Flag className={styles.flag} code={flag} muted={disabled} /> : null}

            {/* Children */}
            {!square && !circle ? <span className={styles.label}>{children}</span> : null}

            {/* Icon right */}
            {icon && iconRight ? <Icon className={styles.icon} name={icon} /> : null}

            {/* Count badge */}
            {count != null ? <span className={styles.count}>{count}</span> : null}
        </>
    );

    if (link) {
        return (
            <Link
                ref={ref}
                to={link}
                title={title}
                className={classes}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : undefined}
                onClick={(event) => {
                    if (!propagate) {
                        event.stopPropagation();
                    }
                    if (isDisabled) {
                        event.preventDefault();
                        return;
                    }
                    if (onClick) {
                        onClick(event);
                    }
                }}
            >
                {content}
            </Link>
        );
    }

    if (href) {
        return (
            <a
                ref={ref}
                href={href}
                title={title}
                className={classes}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : undefined}
                onClick={(event) => {
                    if (!propagate) {
                        event.stopPropagation();
                    }
                    if (isDisabled) {
                        event.preventDefault();
                        return;
                    }
                    if (onClick) {
                        onClick(event);
                    }
                }}
            >
                {content}
            </a>
        );
    }

    return (
        <button
            ref={ref}
            name={name}
            type={type || "button"}
            onClick={handleClick}
            disabled={busy || disabled}
            title={title}
            className={classes}
        >
            {content}
        </button>
    );
});
